import * as React from 'react';
const {useEffect, useReducer, useRef} = React;
import {getPlaceOrderMutation, getRedeemQuotationMutation, getUploadPurchaseOrderMutation} from "./mutations";
import SectionSubmit from "../sectionSubmit";
import {getFilePayload, getOrderInput, getRedeemQuotationInput} from "../helpers";
import {useCartContext} from "../../../../contexts/CartContext";
import {useCheckoutContext} from "../../../../contexts/CheckoutContext";
import {navigate} from "gatsby";
import StepUpForm from "../PaymentDetails/CreditCard/ThreeDSecure/StepUpForm/stepUpForm";
import {is3dsPaymentMethod} from "../PaymentDetails/helpers";
import {reducer} from "./functions/reducer";
import {ApolloError, FetchResult} from '@apollo/client';

export default function Submit({isQuotation, cache, openStepUpIframe, closeStepUpIframe}) {
    // @ts-ignore
    const [checkout] = useCheckoutContext();

    const {cart} = useCartContext();
    const method = checkout.payment_method?.selected_payment_method;
    const is3dsMethod = is3dsPaymentMethod(method);
    const isPurchaseOrderPayment = method === 'purchaseorder';

    const [placeMutation] = isQuotation ?
        getRedeemQuotationMutation()
        : getPlaceOrderMutation();

    const [uploadPoMutation] = getUploadPurchaseOrderMutation();

    const getPlaceData = (response: FetchResult<any>) => {
        return isQuotation ?
            response?.data?.redeemQuotation
            : response?.data?.placeOrder;
    }

    const stepUpFormRef = useRef(null);
    const initiateStepUp = (challengeWindowSize = null) => {
        openStepUpIframe(challengeWindowSize);
        stepUpFormRef.current.dispatchEvent(
            new Event("submit", { cancelable: true, bubbles: true })
        );
    }

    const [state, dispatch] = useReducer(reducer, {
        nextAction: null,
        order: null,
        firstCallMade: false,
        stepUpAvailable: false,
        payerAuthData: {
            accessToken: '',
            stepUpUrl: '',
            challengeWindowSize: null,
        },
        stepUpInProgress: false,
        stepUpCompleted: false,
        secondCallMade: false,
        error: null,
    });

    async function submit(is3dsRetry = false) {
        const poFile = isPurchaseOrderPayment
            ? await getFilePayload(checkout.payment_details.po_file)
            : null;

        if (isPurchaseOrderPayment && !isQuotation && !state.firstCallMade && !is3dsRetry) {
            await submitPO(poFile, cart.id, checkout, uploadPoMutation); // error handled by SectionSubmit
        }

        const shouldCall = is3dsRetry ?
            state.firstCallMade && !state.secondCallMade
            : !state.firstCallMade;

        if (!shouldCall) return;

        const additionalUploads = await Promise.all(
            cache.additional_uploads.map((file: any) => getFilePayload(file))
        );

        const inputVariables = isQuotation
            ? {
                quotationData: getRedeemQuotationInput(
                    cart,
                    checkout,
                    additionalUploads,
                    cache,
                    method,
                    poFile,
                    is3dsRetry
                )
            }
            : {
                orderData: getOrderInput(
                    cart,
                    checkout,
                    additionalUploads,
                    cache,
                    is3dsRetry
                )
            };

        placeMutation({
            variables: inputVariables
        }).then((response) => {
            dispatch({
                type: is3dsRetry ? 'second-call-made': 'first-call-made',
                response: getPlaceData(response)
            });
        }).catch((error: ApolloError) => {
            dispatch({
                type: 'place-mutation-errored',
                error: error
            });
        });
    }

    useEffect(() => {
        if (state.nextAction === 'initiate-step-up') {
            initiateStepUp(state.payerAuthData.challengeWindowSize);
        }

        if (state.nextAction === 'handle-completed-step-up') {
            closeStepUpIframe();
            submit(true).then(() => {});
        }

        if (state.nextAction === 'handle-errored-step-up') {
            closeStepUpIframe();
        }

        if (state.nextAction === 'handle-order-placement') {
            navigate('/checkout/success', {state: {order: state.order}});
        }
    }, [state.nextAction])

    return <>
        {is3dsMethod && <StepUpForm
            formRef={stepUpFormRef}
            formAction={state.payerAuthData.stepUpUrl ?? ''}
            jwt={state.payerAuthData.accessToken ?? ''}
            onCompleted = {() => {dispatch({type: 'step-up-completed'})}}
            onError={() => {dispatch({type: 'step-up-errored'})}}
        />}
        <SectionSubmit callback={submit} disable={!cache.is_confirmation_emails_valid} error={state.error}/>
    </>
}

async function submitPO(
    poFile: any,
    cartId: any,
    checkout: { payment_details: { po_file: any; purchase_order_number: any; additional_information: any; }; },
    mutation: any
) {
    const payload = {
        cartId: cartId,
        purchaseOrderNumber: checkout.payment_details.purchase_order_number,
        poFile: poFile,
        additionalInformation: checkout.payment_details.additional_information ?? null,
    }
    await mutation({variables: payload});
}
